"use client";

import React, { FC, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import SectionHeroVideo from "./SectionHeroVideo";
import SectionPressMarquee from "./SectionPressMarquee";

// Below-the-fold sections are code-split to keep the initial JS bundle
// small enough for the hero to win LCP. They stream in as the user scrolls
// (or as soon as the bundle is idle). No visual change — same DOM order.
const SectionExploreGlobe = dynamic(() => import("./SectionExploreGlobe"), {
  loading: () => <SectionPlaceholder height={720} />,
});
const SectionHowItWorks4Step = dynamic(() => import("./SectionHowItWorks4Step"), {
  loading: () => <SectionPlaceholder height={520} />,
});
const SectionDestinationsCarousel = dynamic(
  () => import("./SectionDestinationsCarousel"),
  { loading: () => <SectionPlaceholder height={560} /> }
);
const SectionEditorialPicks = dynamic(() => import("./SectionEditorialPicks"), {
  loading: () => <SectionPlaceholder height={620} />,
});
const SectionTourCategoriesMosaic = dynamic(
  () => import("./SectionTourCategoriesMosaic"),
  { loading: () => <SectionPlaceholder height={520} /> }
);
const SectionTestimonialsRedesign = dynamic(
  () => import("./SectionTestimonialsRedesign"),
  { loading: () => <SectionPlaceholder height={520} /> }
);
const SectionNewsletter = dynamic(() => import("./SectionNewsletter"), {
  loading: () => <SectionPlaceholder height={360} />,
});

// Reserved vertical space — prevents CLS while a deferred section's
// chunk is still in flight. Heights are eyeball-matched to each section's
// rendered height (within ~10% is enough to avoid layout shift).
const SectionPlaceholder: FC<{ height: number }> = ({ height }) => (
  <div aria-hidden style={{ width: "100%", height }} />
);

/**
 * Wraps each below-the-fold section so it doesn't mount (and pull its JS
 * chunk + run its data fetches) until the user has scrolled within
 * `rootMargin` of it. Without this, all dynamic() chunks load right after
 * hydration even though the user might never scroll there — eating into
 * Lighthouse TBT.
 *
 * Also applies `content-visibility: auto` so the browser skips rendering
 * + painting any section that's offscreen, even after it has mounted.
 * `containIntrinsicSize` reserves the height to keep CLS at 0.
 */
const DeferUntilVisible: FC<{
  reservedHeight: number;
  rootMargin?: string;
  children: React.ReactNode;
}> = ({ reservedHeight, rootMargin = "600px 0px", children }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown) return undefined;
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setShown(true);
      return undefined;
    }
    const node = ref.current;
    if (!node) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [shown, rootMargin]);

  return (
    <div
      ref={ref}
      style={{
        contentVisibility: "auto" as any,
        containIntrinsicSize: `${reservedHeight}px`,
      }}
    >
      {shown ? children : <SectionPlaceholder height={reservedHeight} />}
    </div>
  );
};

/**
 * Desktop home redesign (Guydeez 2026 brand). Composed of independent
 * full-bleed sections — each one wires itself to Redux/API. No props.
 *
 * The mobile experience is unchanged and still rendered by HomePageClient
 * inside its `block lg:hidden` branch.
 */
const DesktopHomeRedesign: FC = () => {
  return (
    <main className="gdx hidden lg:block w-full overflow-hidden">
      <SectionHeroVideo />
      <SectionPressMarquee />
      <DeferUntilVisible reservedHeight={720}>
        <SectionExploreGlobe />
      </DeferUntilVisible>
      <DeferUntilVisible reservedHeight={520}>
        <SectionHowItWorks4Step />
      </DeferUntilVisible>
      <DeferUntilVisible reservedHeight={560}>
        <SectionDestinationsCarousel />
      </DeferUntilVisible>
      <DeferUntilVisible reservedHeight={620}>
        <SectionEditorialPicks />
      </DeferUntilVisible>
      <DeferUntilVisible reservedHeight={520}>
        <SectionTourCategoriesMosaic />
      </DeferUntilVisible>
      <DeferUntilVisible reservedHeight={520}>
        <SectionTestimonialsRedesign />
      </DeferUntilVisible>
      <DeferUntilVisible reservedHeight={360}>
        <SectionNewsletter />
      </DeferUntilVisible>
    </main>
  );
};

export default DesktopHomeRedesign;
